import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { DECIMALS_SHOW } from 'structure/bridge/constants/constants';
import { bridgeAtom } from 'structure/bridge/store/bridgeAtom';
import {
  firstDecimals,
  formatBridgeBalanceAmount,
} from 'structure/bridge/utils/bridgeStateHelpers';
import { formatUnits } from 'viem';
import { LabelSmall } from 'baseui/typography';
import { cx } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import styles from '../bridge.module.css';
import Image from 'next/image';
import { useMemo } from 'react';
import { DEFAULT_BRIDGE_INPUT_DECIMALS } from 'structure/bridge/constants/bridgeTokens';
import { Input } from 'structure/staking/components/form/Input';
import { BridgeNetworkSelection } from '../BridgeNetworkSelection/BridgeNetworkSelection';

export function BridgeDestinationAndAmount({
  tokenBalance,
  allowedNetworks,
}: {
  tokenBalance: bigint;
  allowedNetworks: number[];
}) {
  const { t } = useTranslation('common');
  const [bridgeState] = useAtom(bridgeAtom);

  const value = useMemo(() => {
    if (bridgeState.tokenQuoteLoading) return 'Loading...';
    else {
      return firstDecimals(
        bridgeState.amount -
          parseFloat(
            formatUnits(
              BigInt(bridgeState.tokenFee.toString()),
              bridgeState.token?.decimals
            )
          ),
        DECIMALS_SHOW
      ).toString();
    }
  }, [
    bridgeState.amount,
    bridgeState.tokenFee,
    bridgeState.token,
    bridgeState.tokenQuoteLoading,
  ]);
  const tokenBalanceFormatted = formatBridgeBalanceAmount(
    tokenBalance,
    bridgeState.token.decimals,
    DEFAULT_BRIDGE_INPUT_DECIMALS
  );

  return (
    <div className='rounded-none pt-8'>
      <div className='mb-2 flex items-center justify-between'>
        <LabelSmall
          className={cx(twMerge(styles.label, 'text-white-600'))}
          font={'14px'}
        >
          {t('NETWORK')}
        </LabelSmall>
        <LabelSmall
          className={cx(twMerge(styles.label, 'text-white-600'))}
          font={'14px'}
        >
          {t('AMOUNT')}
        </LabelSmall>
      </div>
      <div className='relative'>
        <div className='flex h-[42px] w-full items-stretch rounded-[20px] bg-[#191A1E]'>
          <BridgeNetworkSelection allowedNetworks={allowedNetworks} embedded />
          <div className='min-w-0 flex-1'>
            <Input
              disabled={true}
              value={value}
              inputMinWidth='48px'
              inputTextAlign='right'
              squareLeftCorners
              endEnhancer={
                <div className='flex cursor-not-allowed flex-row items-center gap-2'>
                  <Image
                    src={bridgeState.token.imageUrl ?? ''}
                    className='rounded-full'
                    alt='TokenIcon'
                    width={24}
                    height={24}
                  />
                  <LabelSmall
                    className={cx(
                      twMerge(
                        styles.bridgeFont,
                        styles.responsiveLabel,
                        'text-lg uppercase'
                      )
                    )}
                  >
                    {bridgeState.token.symbol}
                  </LabelSmall>
                </div>
              }
              placeholder={`${t('AMOUNT')} ${bridgeState.token?.symbol}`}
              clearOnEscape
            />
          </div>
        </div>
      </div>
      <div className='mt-1 text-right text-sm font-medium text-white-400'>
        {t('CURRENT_BALANCE')}: {tokenBalanceFormatted}{' '}
        {bridgeState.token.symbol}
      </div>
    </div>
  );
}
